<template>
    <v-form
        data-cy="reservations-filters-form"
        class="filters-form elevation-1 px-4 py-2 mb-4"
        @keyup.native.enter="filter"
    >
        <v-row dense class="mb-2">
            <v-col cols="12">
                <v-radio-group
                    v-model="tripsFilterModel"
                    :inline="!isXS"
                    density="compact"
                    hide-details
                    class="ma-0 pa-0"
                    @update:model-value="changeActive"
                >
                    <v-radio data-cy="radio-btn-all" :label="$t('all_bookings')" value="all" class="me-4" />
                    <v-radio data-cy="radio-btn-future" :label="$t('future_trips')" value="future" class="me-4" />
                    <v-radio data-cy="radio-btn-past" :label="$t('past_trips')" value="past" class="me-4" />
                    <v-radio
                        data-cy="radio-btn-cancelled"
                        :label="$t('cancelled_trips')"
                        value="cancelled"
                        class="me-2"
                    />
                </v-radio-group>
            </v-col>
        </v-row>
        <v-row dense class="mb-3">
            <v-col v-if="availableFilters.orderId" cols="12" sm="6" md="2">
                <v-text-field
                    v-model="filters.orderId"
                    density="compact"
                    variant="outlined"
                    color="primary"
                    hide-details
                    data-cy="order-id"
                    :label="$t('order_number')"
                />
            </v-col>
            <v-col v-if="availableFilters.travelerName" cols="12" sm="6" md="2">
                <v-text-field
                    v-model="filters.travelerName"
                    density="compact"
                    variant="outlined"
                    color="primary"
                    hide-details
                    data-cy="traveler-name"
                    :label="$t('traveler_name')"
                />
            </v-col>
            <v-col v-if="availableFilters.processStatuses" cols="12" sm="6" md="2">
                <v-select
                    v-model="filters.processStatuses"
                    :items="processStatuses"
                    :label="$t('services_status')"
                    clearable
                    multiple
                    variant="outlined"
                    density="compact"
                    color="primary"
                    hide-details
                    data-cy="process-statuses"
                />
            </v-col>
            <v-col v-if="availableFilters.reservationStatuses" cols="12" sm="6" md="2">
                <v-select
                    v-model="filters.reservationStatuses"
                    :items="reservationStatuses"
                    :label="$t('reservation_status')"
                    clearable
                    multiple
                    variant="outlined"
                    density="compact"
                    color="primary"
                    hide-details
                    data-cy="reservation-statuses"
                />
            </v-col>
            <v-col cols="12" sm="6" md="4" lg="3" xl="2" order="5" order-md="6" order-xl="5">
                <v-switch
                    v-model="filters.filterServices"
                    :label="$t('apply_filter_to_services')"
                    density="compact"
                    color="primary"
                    hide-details
                    data-cy="filter-services"
                    class="pl-2"
                />
            </v-col>
            <v-col cols="12" md="4" lg="3" xl="2" order="6" order-md="5" order-xl="6">
                <v-row dense no-gutters class="fill-height" align="center">
                    <v-col cols="5" class="fill-height px-1">
                        <v-btn
                            data-cy="apply-filter"
                            block
                            class="fill-height"
                            :color="isSomeFiltersSelected ? 'primary' : 'white'"
                            size="small"
                            @click="filter"
                        >
                            {{ $t('filter') }}
                        </v-btn>
                    </v-col>
                    <v-col cols="5" class="fill-height px-1">
                        <v-btn
                            class="fill-height"
                            data-cy="reset-filter"
                            color="white"
                            block
                            size="small"
                            @click="resetFilters"
                        >
                            {{ $t('reset') }}
                        </v-btn>
                    </v-col>
                    <v-col cols="2" class="px-1">
                        <reservations-filters-configuration-modal :filters-list="filtersList" @change="changeFilters" />
                    </v-col>
                </v-row>
            </v-col>
        </v-row>
        <v-expansion-panels dense>
            <v-expansion-panel elevation="0">
                <v-expansion-panel-title class="pl-1 width-auto text-primary">
                    {{ $t('additional_parameters') }}
                </v-expansion-panel-title>
                <v-expansion-panel-text>
                    <div v-if="isTourOperator" class="text-subtitle-2 text-grey">{{ $t('trip_details') }}</div>
                    <v-row dense class="mb-4">
                        <v-col cols="12" md="8">
                            <v-row dense>
                                <v-col v-if="availableFilters.createdDate" cols="12" md="6">
                                    <order-create-daterange
                                        :start-date-label="$t('create_date_from')"
                                        :end-date-label="$t('create_date_to')"
                                        :outlined="false"
                                        :single-line="false"
                                        :no-title="true"
                                        clearable
                                        dense
                                        :_start-date="filters.createdDateFrom"
                                        :_end-date="filters.createdDateTo"
                                        data-cy="created-date-range"
                                        full-width-mobile
                                        @change-start-date="filters.createdDateFrom = $event"
                                        @change-end-date="filters.createdDateTo = $event"
                                    />
                                </v-col>
                                <v-col v-if="availableFilters.serviceTypes" cols="12" md="3">
                                    <v-select
                                        v-model="filters.serviceTypes"
                                        :items="serviceTypes"
                                        :label="$t('service_types')"
                                        clearable
                                        hide-details
                                        multiple
                                        class=""
                                        variant="outlined"
                                        density="compact"
                                        color="primary"
                                        data-cy="service-types"
                                        @update:model-value="handleServiceTypeSelect"
                                    >
                                        <template v-slot:item="{props, item}">
                                            <v-divider v-if="item.raw.type === 'divider'" />
                                            <template v-else>
                                                <v-list-item
                                                    v-show="item.raw.value !== 'productType.ownCharter'"
                                                    v-bind="props"
                                                    :title="item.raw.title"
                                                />
                                            </template>
                                        </template>
                                        <template v-slot:selection="{item, index}">
                                            <span v-if="item.raw.value !== 'productType.ownCharter'">
                                                {{ item.raw.title
                                                }}<span
                                                    v-if="
                                                        item.raw.value === 'productType.flight'
                                                            ? index !== filters.serviceTypes.length - 2
                                                            : index !== filters.serviceTypes.length - 1
                                                    "
                                                    >,</span
                                                >
                                            </span>
                                        </template>
                                    </v-select>
                                </v-col>
                                <v-col v-if="availableFilters.ticketNumber" cols="12" md="3">
                                    <v-text-field
                                        v-model="filters.ticketNumber"
                                        :label="$t('ticket_number')"
                                        variant="outlined"
                                        density="compact"
                                        color="primary"
                                        data-cy="ticket-number"
                                        hide-details
                                        class=""
                                    />
                                </v-col>
                                <v-col v-if="availableFilters.modifiedDate" cols="12" md="6">
                                    <order-create-daterange
                                        :start-date-label="$t('modified_date_from')"
                                        :end-date-label="$t('modified_date_to')"
                                        :outlined="false"
                                        :single-line="false"
                                        :no-title="true"
                                        clearable
                                        dense
                                        data-cy="modified-date-range"
                                        :_start-date="filters.modifiedDateFrom"
                                        :_end-date="filters.modifiedDateTo"
                                        full-width-mobile
                                        @change-start-date="filters.modifiedDateFrom = $event"
                                        @change-end-date="filters.modifiedDateTo = $event"
                                    />
                                </v-col>
                                <v-col v-if="availableFilters.serviceName" cols="12" md="3">
                                    <v-text-field
                                        v-model="filters.serviceName"
                                        :label="$t('service_name')"
                                        variant="outlined"
                                        density="compact"
                                        color="primary"
                                        data-cy="service-name"
                                        hide-details
                                        class=""
                                    />
                                </v-col>
                                <v-col v-if="availableFilters.city" cols="12" md="3">
                                    <city-country-autocomplete v-model="filters.city" dense data-cy="city" class="" />
                                </v-col>
                                <v-col v-if="availableFilters.serviceStartDate" cols="12" md="6">
                                    <order-service-daterange
                                        :start-date-label="$t('start_date_from')"
                                        :end-date-label="$t('start_date_to')"
                                        :outlined="false"
                                        :single-line="false"
                                        :no-title="true"
                                        clearable
                                        dense
                                        data-cy="service-start-date-range"
                                        :_start-date="filters.serviceStartDateFrom"
                                        :_end-date="filters.serviceStartDateTo"
                                        full-width-mobile
                                        @change-start-date="filters.serviceStartDateFrom = $event"
                                        @change-end-date="filters.serviceStartDateTo = $event"
                                    />
                                </v-col>
                                <v-col v-if="availableFilters.promoCode" cols="12" md="3">
                                    <v-text-field
                                        v-model="filters.promoCode"
                                        :label="$t('promocode')"
                                        variant="outlined"
                                        density="compact"
                                        color="primary"
                                        data-cy="promo-code"
                                        hide-details
                                        class=""
                                    />
                                </v-col>
                                <v-col v-if="availableFilters.campaigns" cols="12" md="3">
                                    <v-select
                                        v-model="filters.campaignId"
                                        :items="campaigns"
                                        :label="$t('campaigns')"
                                        clearable
                                        item-value="campaignId"
                                        item-title="name"
                                        density="compact"
                                        color="primary"
                                        data-cy="campaign-id"
                                        hide-details
                                        class="pb-6"
                                    />
                                </v-col>
                                <v-col v-if="availableFilters.classification" cols="12" md="3">
                                    <v-select
                                        v-model="filters.classificationType"
                                        :items="classifications"
                                        :label="$t('filterTitles.classifications')"
                                        clearable
                                        multiple
                                        item-title="name"
                                        item-value="type"
                                        density="compact"
                                        color="primary"
                                        data-cy="classifications"
                                        hide-details
                                        class="pb-6"
                                    />
                                </v-col>
                                <v-col v-if="availableFilters.reservationName" cols="12" md="3">
                                    <v-text-field
                                        v-model="filters.reservationName"
                                        :label="$t('reservation_title')"
                                        variant="outlined"
                                        density="compact"
                                        color="primary"
                                        data-cy="reservation-name"
                                        hide-details
                                        class=""
                                    />
                                </v-col>
                                <v-col v-if="availableFilters.serviceEndDate" cols="12" md="6">
                                    <order-service-daterange
                                        :start-date-label="$t('end_date_from')"
                                        :end-date-label="$t('end_date_to')"
                                        :outlined="false"
                                        :single-line="false"
                                        :no-title="true"
                                        clearable
                                        dense
                                        data-cy="service-end-date-range"
                                        :_start-date="filters.serviceEndDateFrom"
                                        :_end-date="filters.serviceEndDateTo"
                                        full-width-mobile
                                        @change-start-date="filters.serviceEndDateFrom = $event"
                                        @change-end-date="filters.serviceEndDateTo = $event"
                                    />
                                </v-col>
                                <v-col v-if="availableFilters.reservationManager" cols="12" md="3">
                                    <persons-search-autocomplete
                                        v-model="filters.reservationManager"
                                        :company-id="person.companyId"
                                        :label="$t('reservation_manager')"
                                        :show-icon="false"
                                        dense
                                        data-cy="reservation-manager"
                                        class=""
                                    />
                                </v-col>
                                <v-col v-if="availableFilters.loyaltyPoints" cols="12" md="3">
                                    <div class="d-flex">
                                        <v-text-field
                                            v-model="filters.loyaltyPointsMin"
                                            :label="$t('loyalty_from')"
                                            variant="outlined"
                                            density="compact"
                                            color="primary"
                                            data-cy="loyalty-points-min"
                                            hide-details
                                            class="mr-4"
                                        />
                                        <v-text-field
                                            v-model="filters.loyaltyPointsMax"
                                            :label="`${$t('to')}..`"
                                            variant="outlined"
                                            density="compact"
                                            color="primary"
                                            data-cy="loyalty-points-max"
                                            hide-details
                                        />
                                    </div>
                                </v-col>
                                <v-col v-if="availableFilters.lastTicketingDate" cols="12" md="6">
                                    <order-service-daterange
                                        :start-date-label="$t('last_ticketing_date_from')"
                                        :end-date-label="$t('last_ticketing_date_to')"
                                        :outlined="false"
                                        :single-line="false"
                                        :no-title="true"
                                        clearable
                                        dense
                                        data-cy="last-ticketing-date"
                                        :_start-date="filters.lastTicketingDateFrom"
                                        :_end-date="filters.lastTicketingDateTo"
                                        @change-start-date="filters.lastTicketingDateFrom = $event"
                                        @change-end-date="filters.lastTicketingDateTo = $event"
                                    />
                                </v-col>
                                <v-col v-if="availableFilters.serviceManager" cols="12" md="3">
                                    <persons-search-autocomplete
                                        v-model="filters.serviceManager"
                                        :company-id="person.companyId"
                                        :label="$t('service_manager')"
                                        :show-icon="false"
                                        dense
                                        data-cy="service-manager"
                                        class=""
                                    />
                                </v-col>
                                <v-col v-if="availableFilters.departmentId" cols="12" md="3">
                                    <DepartmentSelect
                                        v-model="filters.departmentId"
                                        hide-details
                                        density="compact"
                                        data-cy="department-id"
                                    />
                                </v-col>
                                <v-col v-if="availableFilters.serviceSources" cols="12" md="3">
                                    <v-select
                                        v-model="filters.serviceSources"
                                        :items="serviceSourcesItems"
                                        :label="$t('source_of_service')"
                                        clearable
                                        hide-details
                                        multiple
                                        class=""
                                        variant="outlined"
                                        density="compact"
                                        color="primary"
                                        data-cy="service-sources"
                                    />
                                </v-col>
                                <v-col v-if="availableFilters.bookingMethod" cols="12" md="3">
                                    <v-select
                                        v-model="filters.bookingMethod"
                                        :items="bookingMethods"
                                        :label="$t('booked_from')"
                                        clearable
                                        hide-details
                                        multiple
                                        class=""
                                        variant="outlined"
                                        density="compact"
                                        color="primary"
                                        data-cy="booking-method"
                                    />
                                </v-col>
                                <v-col v-if="isAgency && availableFilters.agentRefNumber" cols="12" md="3">
                                    <v-text-field
                                        v-model="filters.agentRefNumber"
                                        :label="$t('agent_ref')"
                                        density="compact"
                                        hide-details
                                        clearable
                                    />
                                </v-col>
                                <v-col v-if="isAgency && availableFilters.externalRefNumber" cols="12" md="3">
                                    <v-text-field
                                        v-model="filters.externalRefNumber"
                                        :label="$t('external_ref')"
                                        density="compact"
                                        hide-details
                                        clearable
                                    />
                                </v-col>
                                <v-col v-if="availableFilters.serviceDepartmentId" cols="12" md="3">
                                    <DepartmentSelect
                                        v-model="filters.serviceDepartmentId"
                                        :label="$t('product_department')"
                                        hide-details
                                        density="compact"
                                        data-cy="service-department-id"
                                    />
                                </v-col>
                            </v-row>
                        </v-col>
                    </v-row>
                    <template v-if="availableFilters.manualBookingDate">
                        <div class="text-subtitle-2 text-grey mb-2">{{ $t('booking_details') }}</div>
                        <v-row v-if="availableFilters.manualBookingDate" dense class="mb-8">
                            <v-col cols="12" md="4">
                                <order-create-daterange
                                    :start-date-label="$t('booking_date_from')"
                                    :end-date-label="$t('booking_date_to')"
                                    :outlined="false"
                                    :single-line="false"
                                    :no-title="true"
                                    clearable
                                    dense
                                    data-cy="manual-booking-date-range"
                                    :_start-date="filters.manualBookingDateFrom"
                                    :_end-date="filters.manualBookingDateTo"
                                    full-width-mobile
                                    @change-start-date="filters.manualBookingDateFrom = $event"
                                    @change-end-date="filters.manualBookingDateTo = $event"
                                />
                            </v-col>
                        </v-row>
                    </template>
                    <div v-if="isTourOperator && !companyPageMode" class="text-subtitle-2 text-grey">
                        {{ $t('client_details') }}
                    </div>
                    <v-row v-if="isTourOperator" dense class="mb-4">
                        <v-col v-if="availableFilters.client" cols="12" md="2">
                            <client-autocomplete
                                v-model="filters.client"
                                :label="$t('client_name')"
                                :limit="10"
                                :rules="[]"
                                :company-id="filters.customerCompany && filters.customerCompany.id"
                                dense
                                color="primary"
                                data-cy="client"
                                disable-persistent-placeholder
                                class=""
                                @on-blur="onClientNameBlur"
                            />
                        </v-col>
                        <v-col v-if="availableFilters.customerCompany" cols="12" md="2">
                            <customer-company-select-autocomplete
                                v-model="filters.customerCompany"
                                :rules="[]"
                                :label="$t('customer_company')"
                                dense
                                color="primary"
                                data-cy="customer-company"
                                disable-persistent-placeholder
                                class=""
                            />
                        </v-col>
                        <v-col v-if="availableFilters.agent" cols="12" md="2">
                            <persons-search-autocomplete
                                v-model="filters.agent"
                                :company-id="
                                    isTO1
                                        ? (filters.customerCompany && filters.customerCompany.id) || null
                                        : isAgency
                                          ? person.personId
                                          : person.companyId
                                "
                                :label="$t('agent_name')"
                                :show-icon="false"
                                dense
                                color="primary"
                                data-cy="agent"
                                class=""
                            />
                        </v-col>
                        <v-col v-if="availableFilters.clientType" cols="12" md="2">
                            <v-select
                                v-model="filters.clientType"
                                :items="clientTypes"
                                :label="$t('client_type')"
                                clearable
                                hide-details
                                class=""
                                variant="outlined"
                                density="compact"
                                color="primary"
                                data-cy="client-type"
                            />
                        </v-col>
                        <v-col
                            v-if="availableFilters.crmPriority && showPaymentAndInvoiceStatus && showNettoPaymentStatus"
                            md="2"
                            cols="12"
                        >
                            <v-select
                                v-model="filters.crmPriority"
                                :items="crmPriorities"
                                :label="$t('client_priority')"
                                clearable
                                hide-details
                                class=""
                                variant="outlined"
                                density="compact"
                                color="primary"
                                data-cy="crm-priority"
                            />
                        </v-col>
                        <v-col
                            v-if="availableFilters.customerInvoiceStatuses && !$conf.public.account.paymentScheduler"
                            cols="12"
                            md="2"
                        >
                            <v-select
                                v-model="filters.customerInvoiceStatuses"
                                :items="invoiceStatuses"
                                :label="$t('client_invoice_status')"
                                clearable
                                hide-details
                                multiple
                                class=""
                                variant="outlined"
                                density="compact"
                                color="primary"
                                data-cy="customer-invoice-statuses"
                            />
                        </v-col>
                        <v-col v-if="availableFilters.customerPaymentStatuses" cols="12" md="2">
                            <v-select
                                v-model="filters.customerPaymentStatuses"
                                :items="paymentStatuses"
                                :label="$t('client_payment_status')"
                                clearable
                                hide-details
                                multiple
                                class=""
                                variant="outlined"
                                density="compact"
                                color="primary"
                                data-cy="customer-payment-statuses"
                            />
                        </v-col>
                        <v-col v-if="availableFilters.customerPenalty" cols="12" md="2">
                            <v-select
                                v-model="filters.customerPenalty"
                                :items="penalties"
                                :label="$t('client_penalty')"
                                clearable
                                hide-details
                                multiple
                                class=""
                                variant="outlined"
                                density="compact"
                                color="primary"
                                data-cy="customer-penalty"
                            />
                        </v-col>
                        <v-col v-if="availableFilters.paymentMethod" cols="12" md="2">
                            <v-select
                                v-model="filters.paymentMethod"
                                :items="paymentMethods"
                                :label="$t('payment_method')"
                                item-title="name"
                                item-value="name"
                                clearable
                                multiple
                                variant="outlined"
                                density="compact"
                                color="primary"
                                data-cy="payment-method"
                            />
                        </v-col>
                        <v-col v-if="availableFilters.tripOrganizer" cols="12" md="2">
                            <persons-search-autocomplete
                                v-model="filters.tripOrganizer"
                                :company-id="isAgency ? person.personId : person.companyId"
                                :label="$t('trip_organizer_name')"
                                :show-icon="false"
                                dense
                                data-cy="trip-organizer"
                                class=""
                            />
                        </v-col>
                        <v-col
                            v-if="
                                availableFilters.corporatePolicyViolated &&
                                showPaymentAndInvoiceStatus &&
                                showNettoPaymentStatus
                            "
                            md="2"
                            cols="12"
                        >
                            <v-select
                                v-model="filters.corporatePolicyViolated"
                                :items="booleanItems"
                                :label="$t('corporate_policy_violation')"
                                clearable
                                hide-details
                                class=""
                                variant="outlined"
                                density="compact"
                                color="primary"
                                data-cy="corporate-policy-violated"
                            />
                        </v-col>
                        <v-col v-if="availableFilters.waitAuthorize" cols="12" md="2">
                            <v-select
                                v-model="filters.waitAuthorize"
                                :items="booleanItems"
                                :label="`${$t('approval_required')}?`"
                                clearable
                                hide-details
                                class=""
                                variant="outlined"
                                density="compact"
                                color="primary"
                                data-cy="wait-authorize"
                            />
                        </v-col>
                        <v-col v-if="availableFilters.waitingApprovalFromMyself" cols="12" md="2">
                            <v-checkbox
                                v-model="filters.waitingApprovalFromMyself"
                                hide-details
                                color="primary"
                                data-cy="waitingApproval-from-myself"
                            >
                                <template v-slot:label>
                                    <div v-text="$t('waiting_approval_from_myself')" />
                                </template>
                            </v-checkbox>
                        </v-col>
                        <v-col v-if="availableFilters.agentCompanyConsortium" cols="12" md="2">
                            <v-select
                                v-model="filters.agentCompanyConsortium"
                                :label="$t('agent_consortium')"
                                density="compact"
                                :items="consortiumns"
                                clearable
                                hide-details
                                color="primary"
                                data-cy="agent-company-consortium"
                                class=""
                            />
                        </v-col>
                        <v-col v-if="availableFilters.agentCompanySubconsortium" cols="12" md="2">
                            <v-select
                                v-model="filters.agentCompanySubconsortium"
                                :label="$t('agent_sub_consortium')"
                                variant="outlined"
                                density="compact"
                                :items="subConsortiums"
                                clearable
                                hide-details
                                color="primary"
                                data-cy="agent-company-subconsortium"
                                class=""
                            />
                        </v-col>
                        <v-col v-if="availableFilters.agencyRegionCode" cols="12" md="2">
                            <v-select
                                v-model="filters.agencyRegionCode"
                                :items="agencyRegionCodeItems"
                                :label="`${$t('agent_region_code')}`"
                                clearable
                                density="compact"
                                hide-details
                                color="primary"
                                data-cy="agency-region-code"
                                class=""
                            />
                        </v-col>
                        <v-col v-if="availableFilters.customerPaymentDueDate" cols="12" md="4">
                            <order-service-daterange
                                :start-date-label="$t('customer_payment_due_date_from')"
                                :end-date-label="$t('customer_payment_due_date_to')"
                                :outlined="false"
                                :single-line="false"
                                :no-title="true"
                                clearable
                                dense
                                data-cy="customer-payment-due-date-range"
                                :_start-date="filters.customerPaymentDueDateFrom"
                                :_end-date="filters.customerPaymentDueDateTo"
                                @change-start-date="filters.customerPaymentDueDateFrom = $event"
                                @change-end-date="filters.customerPaymentDueDateTo = $event"
                            />
                        </v-col>
                    </v-row>
                    <div v-if="isTourOperator" class="text-subtitle-2 text-grey">{{ $t('supplier_details') }}</div>
                    <v-row v-if="isTourOperator" dense>
                        <v-col v-if="availableFilters.supplier" cols="12" md="2">
                            <supplier-select-autocomplete
                                v-model="filters.supplier"
                                :rules="[]"
                                :label="$t('supplier_name')"
                                dense
                                data-cy="tour-operator-supplier"
                                disable-persistent-placeholder
                            />
                        </v-col>
                        <v-col v-if="availableFilters.supplierRef" cols="12" md="2">
                            <v-text-field
                                v-model="filters.supplierRef"
                                :label="$t('supplier_ref')"
                                variant="outlined"
                                density="compact"
                                color="primary"
                                data-cy="tour-operator-supplier-ref"
                                hide-details
                                class=""
                            />
                        </v-col>
                        <v-col v-if="availableFilters.externalRefNumber" cols="12" md="2">
                            <v-text-field
                                v-model="filters.externalRefNumber"
                                :label="$t('external_ref')"
                                density="compact"
                                hide-details
                                clearable
                            />
                        </v-col>
                        <v-col v-if="availableFilters.channelManagerCode" cols="12" md="2">
                            <v-select
                                v-model="filters.channelManagerCode"
                                :items="channelManagerCodeItems"
                                :label="$t('channel_manager_name')"
                                clearable
                                variant="outlined"
                                density="compact"
                                color="primary"
                                data-cy="channel-manager-code"
                            />
                        </v-col>
                        <v-col v-if="availableFilters.chmPartnerBookingNumber" cols="12" md="2">
                            <v-text-field
                                v-model="filters.chmPartnerBookingNumber"
                                :label="$t('channel_manager_ref')"
                                clearable
                                variant="outlined"
                                density="compact"
                                hide-details
                                color="primary"
                                data-cy="chm-partner-booking-number"
                                class=""
                            />
                        </v-col>
                        <v-col
                            v-if="availableFilters.supplierInvoiceStatuses && !$conf.public.account.paymentScheduler"
                            md="2"
                            cols="12"
                        >
                            <v-select
                                v-model="filters.supplierInvoiceStatuses"
                                :items="invoiceStatuses"
                                :label="$t('supplier_invoice_status')"
                                clearable
                                multiple
                                variant="outlined"
                                density="compact"
                                color="primary"
                                data-cy="supplier-invoice-statuses"
                            />
                        </v-col>
                        <v-col v-if="availableFilters.supplierPaymentStatuses" md="2" cols="12">
                            <v-select
                                v-model="filters.supplierPaymentStatuses"
                                :items="paymentStatuses"
                                :label="$t('supplier_payment_status')"
                                clearable
                                multiple
                                hide-details
                                variant="outlined"
                                density="compact"
                                color="primary"
                                data-cy="supplier-payment-statuses"
                            />
                        </v-col>
                        <v-col v-if="availableFilters.supplierPenalty" md="2" cols="12">
                            <v-select
                                v-model="filters.supplierPenalty"
                                :items="penalties"
                                :label="$t('supplier_penalty')"
                                clearable
                                multiple
                                hide-details
                                variant="outlined"
                                density="compact"
                                color="primary"
                                data-cy="supplier-penalty"
                            />
                        </v-col>
                        <v-col v-if="availableFilters.noSupplierRef" md="2" cols="12">
                            <v-checkbox
                                v-model="filters.noSupplierRef"
                                hide-details
                                color="primary"
                                data-cy="no-supplier-ref"
                            >
                                <template v-slot:label>
                                    <div v-text="$t('empty_supplier_ref')" />
                                </template>
                            </v-checkbox>
                        </v-col>
                        <v-col v-if="availableFilters.supplierPaymentDueDate" cols="12" md="4">
                            <order-service-daterange
                                :start-date-label="$t('supplier_payment_due_date_from')"
                                :end-date-label="$t('supplier_payment_due_date_to')"
                                :outlined="false"
                                :single-line="false"
                                :no-title="true"
                                clearable
                                dense
                                data-cy="supplier-payment-due-date-range"
                                :_start-date="filters.supplierPaymentDueDateFrom"
                                :_end-date="filters.supplierPaymentDueDateTo"
                                @change-start-date="filters.supplierPaymentDueDateFrom = $event"
                                @change-end-date="filters.supplierPaymentDueDateTo = $event"
                            />
                        </v-col>
                    </v-row>
                    <v-row v-if="isAgency || isCorporate" dense>
                        <v-col v-if="availableFilters.paymentMethod" cols="12" md="2">
                            <v-select
                                v-model="filters.paymentMethod"
                                :items="paymentMethods"
                                :label="$t('payment_method')"
                                item-title="name"
                                item-value="name"
                                clearable
                                multiple
                                variant="outlined"
                                density="compact"
                                color="primary"
                                data-cy="supplier-payment-method"
                            />
                        </v-col>
                        <v-col v-if="availableFilters.corporatePolicyViolated" cols="12" md="2">
                            <v-select
                                v-model="filters.corporatePolicyViolated"
                                :items="booleanItems"
                                :label="$t('corporate_policy_violation')"
                                clearable
                                hide-details
                                variant="outlined"
                                density="compact"
                                color="primary"
                                data-cy="corporate-policy-violated"
                            />
                        </v-col>
                        <v-col v-if="availableFilters.waitAuthorize" cols="12" md="2">
                            <v-select
                                v-model="filters.waitAuthorize"
                                :items="booleanItems"
                                :label="`${$t('approval_required')}?`"
                                clearable
                                hide-details
                                class=""
                                variant="outlined"
                                density="compact"
                                color="primary"
                                data-cy="wait-authorize"
                            />
                        </v-col>
                        <v-col v-if="availableFilters.supplier" cols="12" md="2">
                            <supplier-select-autocomplete
                                v-model="filters.supplier"
                                :rules="[]"
                                :label="$t('supplier_name')"
                                dense
                                disable-persistent-placeholder
                                data-cy="supplier"
                            />
                        </v-col>
                        <v-col v-if="availableFilters.waitingApprovalFromMyself" cols="12" md="2">
                            <v-checkbox
                                v-model="filters.waitingApprovalFromMyself"
                                hide-details
                                color="primary"
                                data-cy="waiting-approval-from-myself"
                            >
                                <template v-slot:label>
                                    <div v-text="$t('waiting_approval_from_myself')" />
                                </template>
                            </v-checkbox>
                        </v-col>
                    </v-row>
                    <v-row v-if="isSupplier || isAgency || isCorporate" dense class="align-center">
                        <v-col v-if="availableFilters.supplierRef" cols="12" md="2">
                            <v-text-field
                                v-model="filters.supplierRef"
                                :label="$t('supplier_ref')"
                                variant="outlined"
                                density="compact"
                                color="primary"
                                data-cy="supplier-ref"
                                hide-details
                                class=""
                            />
                        </v-col>
                        <v-col v-if="availableFilters.noSupplierRef" cols="12" md="2">
                            <v-checkbox
                                v-model="filters.noSupplierRef"
                                hide-details
                                color="primary"
                                data-cy="no-supplier-ref"
                            >
                                <template v-slot:label>
                                    <div v-text="$t('empty_supplier_ref')" />
                                </template>
                            </v-checkbox>
                        </v-col>
                    </v-row>
                </v-expansion-panel-text>
            </v-expansion-panel>
        </v-expansion-panels>
    </v-form>
</template>

<script lang="ts">
    import {Component, Emit, Prop, Setup, Vue} from 'vue-facing-decorator'
    import {clone, isEqual} from '@/utils/helpers'
    import CityAutocomplete from '~/components/search/forms/CityAutocomplete.vue'
    import CityCountryAutocomplete from '~/components/search/forms/CityCountryAutocomplete.vue'
    import OrderCreateDaterange from '~/components/account/filters/OrderCreateDaterange.vue'
    import OrderServiceDaterange from '~/components/account/filters/OrderServiceDaterange.vue'
    import SupplierSelectAutocomplete from '@/components/search/forms/SupplierSelectAutocomplete.vue'
    import CustomerCompanySelectAutocomplete from '@/components/search/forms/CustomerCompanySelectAutocomplete.vue'
    import ClientAutocomplete from '@/components/account/modals/newOrderRequest/ClientAutocomplete.vue'
    import PersonsSearchAutocomplete from '@/components/search/forms/PersonsSearchAutocomplete.vue'
    import ReservationsFiltersConfigurationModal from '@/components/account/filters/ReservationsFiltersConfigurationModal.vue'
    import {format} from 'date-fns'
    import DepartmentSelect from '~/components/account/snippets/DepartmentSelect.vue'
    import {valuesIn} from 'lodash-es'
    import {productsMap} from '~src/utils/authorization-info-utils'
    import {appInstance} from '~/utils/app-accessor'

    @Component({
        components: {
            DepartmentSelect,
            CityAutocomplete,
            CityCountryAutocomplete,
            OrderCreateDaterange,
            OrderServiceDaterange,
            SupplierSelectAutocomplete,
            CustomerCompanySelectAutocomplete,
            ClientAutocomplete,
            PersonsSearchAutocomplete,
            ReservationsFiltersConfigurationModal,
        },
    })
    export class OrderFilters extends Vue {
        @Prop({required: true}) filtersList
        @Prop({required: true}) hiddenFilters
        @Prop({default: false, type: Boolean}) companyPageMode: boolean
        @Prop({default: false, type: Boolean}) productPageMode: boolean

        filters = {}
        paymentMethods = []
        tripsFilterModel = 'all'
        consortiumns = []
        subConsortiums = []
        classifications = []

        @Emit()
        changeFilters(filters) {
            return filters
        }

        async created() {
            if (this.isTourOperator) {
                try {
                    this.paymentMethods = await this.$api.paymentMethods.get({'is active': true})

                    if (this.availableFilters.agentCompanyConsortium) {
                        this.consortiumns = (await this.$api.consortiums.get()).consortiums
                    }

                    if (this.availableFilters.agentCompanySubconsortium) {
                        this.subConsortiums = (await this.$api.subConsortiums.get()).subConsortiums
                    }

                    // eslint-disable-next-line no-empty
                } catch (e) {}
            }
        }

        async mounted() {
            await this.$store.restored
            this.load()
            if (this.availableFilters.campaigns) {
                await runtimeStore.loadCampaigns()
            }
            if (this.availableFilters.classification) {
                await this.loadClassifications()
            }
        }

        async loadClassifications() {
            try {
                const {classificationTypes} = await this.$api.orderClassificationTypes.get()
                this.classifications = classificationTypes.sort((a, b) => a.name.localeCompare(b.name))
            } catch (e) {
                this.classifications = []
            }
        }

        load() {
            const filters = clone(persistentStore.orderFilters)

            if (this.$conf.public.account.plainPaymentStatus || this.$conf.public.account.paymentScheduler) {
                filters.customerPaymentStatuses = runtimeStore.plainPaymentStatuses(filters.customerPaymentStatuses)
                filters.supplierPaymentStatuses = runtimeStore.plainPaymentStatuses(filters.supplierPaymentStatuses)
            }

            this.filters = filters
        }

        clearTripsFilter(key) {
            if (
                (key === 'reservationStatuses' && this.tripsFilterModel === 'cancelled') ||
                (key === 'serviceStartDateFrom' && this.tripsFilterModel === 'future') ||
                (key === 'serviceStartDateTo' && this.tripsFilterModel === 'past')
            ) {
                this.tripsFilterModel = 'all'
            }
        }

        filter() {
            this.filters.active = persistentStore.orderFilters.active
            const orderFilters = clone(this.filters)

            if (this.$conf.public.account.plainPaymentStatus || this.$conf.public.account.paymentScheduler) {
                const reducePaymentStatuses = statuses =>
                    statuses.reduce((statuses, status) => {
                        if (status === 'NOT_PAID') {
                            statuses = [
                                ...statuses,
                                'NO_BILL',
                                'BILL',
                                'IN_TRANSIT',
                                'OVERDUE',
                                'OVERDUE',
                                'NO_FULLY_BILL',
                                'INVOICE_WAITING',
                            ]
                        } else {
                            statuses.push(status)
                        }
                        return statuses
                    }, [])

                orderFilters.customerPaymentStatuses = reducePaymentStatuses(orderFilters.customerPaymentStatuses)
                orderFilters.supplierPaymentStatuses = reducePaymentStatuses(orderFilters.supplierPaymentStatuses)
            }

            persistentStore.SET_ORDER_FILTERS(orderFilters)
            this.$emit('filter')
        }

        @Emit()
        resetFilters() {
            persistentStore.CLEAR_ORDER_FILTERS()
            if (this.companyPageMode) {
                const personId = this.$route.params.id
                persistentStore.SET_ORDER_FILTERS(Object.assign({}, persistentStore.orderFilters, {client: {personId}}))
            }
            if (this.productPageMode) {
                persistentStore.SET_ORDER_FILTERS(
                    Object.assign({}, persistentStore.orderFilters, {
                        productId: supplierStore.contract.id,
                        filterServices: true,
                    })
                )
            }

            this.load()
            this.$emit('filter')
            this.tripsFilterModel = 'all'
        }

        changeActive(val) {
            const orderFilters = clone(this.filters)

            orderFilters.reservationStatuses = []
            orderFilters.serviceStartDateFrom = null
            orderFilters.serviceStartDateTo = null

            if (val === 'cancelled') orderFilters.reservationStatuses = ['CANCELLED']
            if (val === 'future') orderFilters.serviceStartDateFrom = format(new Date(), 'yyyy-MM-dd')
            if (val === 'past')
                orderFilters.serviceStartDateTo = format(this.$dateFns.subDays(new Date(), 1), 'yyyy-MM-dd')

            this.filters = orderFilters
        }

        get availableFilters() {
            const filters = {}
            this.filtersList.forEach(item => {
                filters[item.value] = !this.hiddenFilters?.filters?.includes(item.value)
            })

            return filters
        }

        handleServiceTypeSelect(value) {
            if (value.includes('productType.flight')) {
                this.filters.serviceTypes = [...new Set([...value, 'productType.ownCharter'])]
            } else if (
                !value.includes('productType.flight') &&
                (this.filters.serviceTypes.includes('productType.flight') ||
                    this.filters.serviceTypes.includes('productType.ownCharter'))
            ) {
                this.filters.serviceTypes = value.filter(
                    item => item !== 'productType.flight' && item !== 'productType.ownCharter'
                )
            } else {
                this.filters.serviceTypes = value
            }
        }

        onClientNameBlur(val) {
            if (!this.filters.client && val?.length) {
                this.filters.client = {
                    firstName: val,
                }
            }
        }

        get serviceTypes() {
            const separatedTypes = ['customComplexTour', 'dynamicTour', 'complexTour']
            const serviceTypesAvailable = authStore.availableProducts.filter(value => !separatedTypes.includes(value))
            const orderArray = this.$conf.public.activeProducts.map(type => productsMap[type]).filter(el => !!el)
            serviceTypesAvailable.sort((a, b) => {
                const indexA = orderArray.indexOf(a)
                const indexB = orderArray.indexOf(b)

                if (indexA === -1) return 1
                if (indexB === -1) return -1

                return indexA - indexB
            })

            const items = serviceTypesAvailable.map(value => ({
                value: `productType.${value}`,
                title: this.$t(`productType.${value}`),
            }))

            items.push({type: 'divider'})

            items.push(
                ...separatedTypes
                    .filter(serviceType => authStore.availableProducts.includes(serviceType))
                    .map(value => ({
                        value: `productType.${value}`,
                        title: this.$t(`productType.${value}`),
                    }))
            )

            items.push({
                value: 'productType.other',
                title: this.$t('productType.other'),
            })

            return items
        }

        get bookingMethods() {
            return [
                {value: 'ALLOTMENT', title: this.$t('booking_methods.ALLOTMENT')},
                {value: 'ON_REQUEST', title: this.$t('booking_methods.ON_REQUEST')},
                {value: 'MANUALLY_AMENDED', title: this.$t('booking_methods.MANUALLY_AMENDED')},
            ]
        }

        get serviceSourcesItems() {
            return [
                {value: 'SELFOPERATED', title: this.$t('service_sources.SELFOPERATED')},
                {value: 'API', title: this.$t('service_sources.API')},
                {value: 'OFFLINE', title: this.$t('service_sources.OFFLINE')},
            ]
        }

        // TODO
        get processStatuses() {
            const processStatuses = [
                'CONFIRMED',
                'CONFIRMATION_PENDING',
                'CANCELED',
                'CANCELLATION_PENDING',
                'MODIFICATION_PENDING',
                'REJECTED',
                'QUOTE',
                'ERROR',
                'PRECONFIRMED',
                'PENDING_TICKETING',
                'ESTIMATED',
            ]

            return processStatuses.map(value => ({value, title: this.$t(`bookingStatus.${value}`)}))
        }

        get reservationStatuses() {
            const processStatuses = ['NEW', 'IN_PROGRESS', 'COMPLETED', 'QUOTE']

            if (this.$conf.public.reservationStatuses.completedAndChecked) processStatuses.push('COMPLETED_AND_CHECKED')
            processStatuses.push('CANCELLED')
            if (this.$conf.public.reservationStatuses.archived) processStatuses.push('ARCHIVED')
            if (this.$conf.public.reservationStatuses.vouchered) processStatuses.push('VOUCHERED')

            return processStatuses.map(value => ({value, title: this.$t(`orderStatus.${value}`)}))
        }

        get clientTypes() {
            const processStatuses = ['DIRECT_SALES', 'TA', 'CORPORATE']

            return processStatuses.map(value => ({value, title: this.$t(`clientTypes.${value}`)}))
        }

        get crmPriorities() {
            const processStatuses = ['VIP', 'HIGH', 'NORMAL', 'LOW']

            return processStatuses.map(value => ({value, title: this.$t(`crmPriority.${value}`)}))
        }

        get channelManagerCodeItems() {
            const items = ['SM', 'OPEN_GDS', 'RateDock']

            return items.map(value => ({value, title: this.$t(`channelManagerCodes.${value}`)}))
        }

        get booleanItems() {
            return [
                {value: true, title: this.$t('yes')},
                {value: false, title: this.$t('no')},
            ]
        }

        get agencyRegionCodeItems() {
            return []
        }

        get paymentStatuses() {
            if (!this.$conf.public.account.plainPaymentStatus && !this.$conf.public.account.paymentScheduler) {
                const paymentStatuses = ['OVERPAID', 'PAID', 'PARTIAL_PAID', 'IN_TRANSIT', 'OVERDUE']

                return paymentStatuses.map(value => ({value, title: this.$t(`paymentStatus.${value}`)}))
            }

            return ['OVERPAID', 'PAID', 'PARTIAL_PAID', 'NOT_PAID'].map(value => ({
                value,
                title: this.$t(`plainPaymentStatus.${value}`),
            }))
        }

        get invoiceStatuses() {
            const invoiceStatuses = ['NO_BILL', 'BILL', 'NO_FULLY_BILL', 'INVOICE_WAITING']

            return invoiceStatuses.map(value => ({value, title: this.$t(`paymentStatus.${value}`)}))
        }

        get penalties() {
            const penalties = ['FREE', 'PAID', 'INFO', 'WARNING', 'ERROR']

            return penalties.map(value => ({value, title: this.$t(`penalty.${value}`)}))
        }

        get person() {
            return authStore.person
        }

        get isTourOperator() {
            return authStore.isTourOperator
        }

        get isAgency() {
            return authStore.isAgency
        }

        get isTO1() {
            return authStore.isTO1
        }

        get isCorporate() {
            return authStore.isCorporate
        }

        get isSupplier() {
            return authStore.isSupplier
        }

        get showPaymentAndInvoiceStatus() {
            return authStore.showPaymentAndInvoiceStatus
        }

        get showNettoPaymentStatus() {
            return true
        }

        get isSomeFiltersSelected() {
            const filtersStoreClone = clone(persistentStore.orderFilters)

            return !isEqual(filtersStoreClone, this.filters)
        }

        get stateFilters() {
            return persistentStore.orderFilters
        }

        get isXS() {
            return this.$breakpoint.xs
        }

        get isMobile() {
            return this.$breakpoint.smAndDown
        }

        get campaigns() {
            return runtimeStore.campaigns
        }
    }

    export default OrderFilters
</script>

<style scoped lang="scss">
    :deep(.filters-form) {
        .v-expansion-panel-header {
            min-height: auto;
            padding: 0;
            margin: 0 0 10px;
            font-size: 12px;
            color: rgba(0, 0, 0, 0.6);
        }
        .v-expansion-panel-content__wrap {
            padding: 0;
        }

        .v-text-field .v-label,
        .v-radio .v-label,
        .v-input--switch .v-label,
        .v-text-field input,
        .v-select__selections {
            font-size: 12px;
        }
    }
</style>
